import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from "@mui/material";
import { alpha, keyframes, useTheme } from "@mui/material/styles";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import { useTranslation } from "react-i18next";
import { pagePrimaryButtonSx } from "../../styles/pageModernStyles";
import {
  bob,
  motionSafe,
  rise,
  studioDialogBackdropSx,
  studioDialogPaperSx,
  swapSlide,
} from "../AI/studioDialogMotion";
import { normalizeOrgBrand } from "./skillAgentOrgBrand";

const NML_ACCENT = "#6a1b9a";
const STAGE_ACCENT = "#00897b";

const trailDots = keyframes`
  0%, 100% { opacity: 0.25; transform: translateX(0); }
  50% { opacity: 1; transform: translateX(4px); }
`;

function brandAccent(brand) {
  return normalizeOrgBrand(brand) === "NML" ? NML_ACCENT : STAGE_ACCENT;
}

function BrandPill({ brand, active, pulse }) {
  const accent = brandAccent(brand);
  return (
    <Box
      sx={{
        minWidth: 92,
        px: 1.5,
        py: 1.1,
        borderRadius: 2.25,
        border: "2px solid",
        borderColor: active ? accent : alpha(accent, 0.28),
        bgcolor: active ? alpha(accent, 0.14) : alpha(accent, 0.04),
        textAlign: "center",
        boxShadow: active ? `0 10px 22px ${alpha(accent, 0.22)}` : "none",
        animation: pulse
          ? `${bob} 1.6s ease-in-out infinite`
          : `${rise} 320ms ease both`,
        ...motionSafe,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: "block",
          fontWeight: 800,
          letterSpacing: 0.08,
          textTransform: "uppercase",
          fontSize: "0.62rem",
          color: accent,
          mb: 0.15,
        }}
      >
        Empresa
      </Typography>
      <Typography
        variant="subtitle1"
        fontWeight={900}
        sx={{ color: accent, lineHeight: 1.1 }}
      >
        {brand}
      </Typography>
    </Box>
  );
}

function MoveStage({ fromBrand, toBrand }) {
  const from = normalizeOrgBrand(fromBrand);
  const to = normalizeOrgBrand(toBrand);
  const toAccent = brandAccent(to);

  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        mt: 1.25,
        mb: 0.35,
        px: 1,
        py: 1.5,
        borderRadius: 2.25,
        overflow: "hidden",
        bgcolor: alpha(toAccent, 0.06),
        border: "1px solid",
        borderColor: alpha(toAccent, 0.2),
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        animation: `${rise} 300ms ease both`,
        ...motionSafe,
      }}
    >
      <BrandPill brand={from} active={false} />
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.35,
          minWidth: 0,
        }}
      >
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              bgcolor: alpha(toAccent, 0.35 + i * 0.15),
              animation: `${trailDots} 1.1s ease-in-out infinite`,
              animationDelay: `${i * 0.12}s`,
              ...motionSafe,
            }}
          />
        ))}
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            bgcolor: alpha(toAccent, 0.16),
            border: "1.5px solid",
            borderColor: alpha(toAccent, 0.45),
            color: toAccent,
            animation: `${swapSlide} 1.8s ease-in-out infinite`,
            ...motionSafe,
          }}
        >
          <ArrowForwardOutlinedIcon sx={{ fontSize: 22 }} />
        </Box>
        {[0, 1, 2].map((i) => (
          <Box
            key={`r${i}`}
            sx={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              bgcolor: alpha(toAccent, 0.65 - i * 0.15),
              animation: `${trailDots} 1.1s ease-in-out infinite`,
              animationDelay: `${0.35 + i * 0.12}s`,
              ...motionSafe,
            }}
          />
        ))}
      </Box>
      <BrandPill brand={to} active pulse />
    </Box>
  );
}

function kindLabel(t, kind) {
  switch (kind) {
    case "meeting":
      return t("skillAgent.tree.folderMeetings", { defaultValue: "Reunión" });
    case "task":
      return t("skillAgent.dialog.hubCreate.task.title", {
        defaultValue: "Diseño",
      });
    case "search":
      return t("skillAgent.dialog.hubCreate.search.title", {
        defaultValue: "Search",
      });
    case "moodboard":
      return t("skillAgent.dialog.hubCreate.moodboard.title", {
        defaultValue: "Moodboard",
      });
    case "rapport":
      return t("skillAgent.dialog.hubCreate.rapport.title", {
        defaultValue: "Rapport",
      });
    case "clientInfo":
      return t("skillAgent.dialog.hubCreate.clientInfo.title", {
        defaultValue: "Client info",
      });
    default:
      return t("skillAgent.dialog.moveOrgBrandItem", {
        defaultValue: "Elemento",
      });
  }
}

function itemTitle(kind, item) {
  if (!item) return "—";
  if (kind === "meeting") {
    return String(
      item.title || item.meetingTitle || `Reunión #${item.id}`,
    ).trim();
  }
  return String(
    item.Titulo || item.Name || item.title || item.label || `#${item.id}`,
  ).trim();
}

/**
 * Confirmación Compás para mover un ítem NML ↔ STAGE (animación de traslado).
 */
export default function SkillAgentMoveOrgBrandDialog({
  open,
  onClose,
  kind = "",
  item = null,
  targetBrand = "STAGE",
  saving = false,
  error = "",
  onConfirm,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const fromBrand = normalizeOrgBrand(item?.OrgBrand || item?.orgBrand);
  const toBrand = normalizeOrgBrand(targetBrand);
  const accent = brandAccent(toBrand);
  const title = itemTitle(kind, item);
  const kindText = kindLabel(t, kind);

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      keepMounted={false}
      transitionDuration={180}
      slotProps={{
        backdrop: { sx: studioDialogBackdropSx },
      }}
      BackdropProps={{ sx: studioDialogBackdropSx }}
      PaperProps={{
        sx: {
          ...studioDialogPaperSx,
          animation: open ? `${rise} 280ms ease both` : undefined,
          ...motionSafe,
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.25,
          borderBottom: "1px solid",
          borderColor: "divider",
          background: `linear-gradient(135deg, ${alpha(accent, 0.16)} 0%, ${alpha(
            accent,
            0.02,
          )} 55%)`,
          animation: `${rise} 300ms ease both`,
          ...motionSafe,
        }}
      >
        <Typography variant="overline" sx={{ color: accent, fontWeight: 800 }}>
          {t("skillAgent.dialog.moveOrgBrandEyebrow", {
            defaultValue: "Compás · Empresa",
          })}
        </Typography>
        <Typography variant="h6" fontWeight={800} lineHeight={1.25}>
          {t("skillAgent.dialog.moveOrgBrandTitle", {
            defaultValue: "Mover a {{brand}}",
            brand: toBrand,
          })}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t("skillAgent.dialog.moveOrgBrandLead", {
            defaultValue:
              "«{{title}}» pasa de {{from}} a {{to}}. El árbol Compás lo recoloca al instante.",
            title,
            from: fromBrand,
            to: toBrand,
          })}
        </Typography>
        <MoveStage fromBrand={fromBrand} toBrand={toBrand} />
      </Box>

      <DialogContent sx={{ px: 2.5, pt: 1.75, pb: 1 }}>
        <Box
          sx={{
            px: 1.5,
            py: 1.2,
            borderRadius: 2,
            bgcolor: isDark ? alpha(accent, 0.12) : alpha(accent, 0.07),
            border: "1px solid",
            borderColor: alpha(accent, 0.26),
          }}
        >
          <Typography
            variant="caption"
            fontWeight={800}
            sx={{ color: accent, letterSpacing: 0.04 }}
          >
            {kindText}
          </Typography>
          <Typography
            variant="body2"
            fontWeight={700}
            noWrap
            title={title}
            sx={{ mt: 0.25 }}
          >
            {title}
          </Typography>
        </Box>
        {error ? (
          <Alert severity="error" sx={{ borderRadius: 2, mt: 1.25 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2, pt: 0.5, gap: 1 }}>
        <Button onClick={onClose} disabled={saving} color="inherit">
          {t("skillAgent.common.cancel", { defaultValue: "Cancelar" })}
        </Button>
        <Button
          variant="contained"
          disabled={saving || fromBrand === toBrand}
          onClick={onConfirm}
          startIcon={
            saving ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <SwapHorizOutlinedIcon />
            )
          }
          sx={{
            ...pagePrimaryButtonSx,
            bgcolor: accent,
            "&:hover": {
              bgcolor: toBrand === "NML" ? "#4a148c" : "#00695c",
            },
          }}
        >
          {t("skillAgent.dialog.moveOrgBrandSubmit", {
            defaultValue: "Mover a {{brand}}",
            brand: toBrand,
          })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
